/**
 * Feature Flags Utility
 * 
 * Supports both build-time environment variables and runtime feature flags.
 * Runtime flags take precedence over build-time flags (MUST FIX #4).
 * 
 * This enables:
 * - Kill-switch capability without redeploy
 * - Phased rollout (10% → 50% → 100%)
 * - A/B testing
 */

/**
 * Runtime feature flag storage
 * In a production environment, this would typically be fetched from:
 * - API endpoint
 * - Configuration service
 * - Feature flag provider (LaunchDarkly, etc.)
 * 
 * For now, we use sessionStorage as a simple runtime override mechanism.
 * In production, replace this with actual feature flag service integration.
 */
const RUNTIME_FLAGS_KEY = 'feature_flags';

/**
 * Get runtime feature flags from storage
 */
function getRuntimeFlags(): Record<string, boolean | string> {
  try {
    const stored = sessionStorage.getItem(RUNTIME_FLAGS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.warn('[FeatureFlags] Failed to parse runtime flags:', e);
  }
  return {};
}

/**
 * Set runtime feature flag
 * This allows runtime override without redeploy (MUST FIX #4)
 * 
 * @param flagName - Feature flag name
 * @param value - Feature flag value
 */
export function setRuntimeFeatureFlag(flagName: string, value: boolean | string): void {
  try {
    const flags = getRuntimeFlags();
    flags[flagName] = value;
    sessionStorage.setItem(RUNTIME_FLAGS_KEY, JSON.stringify(flags));
  } catch (e) {
    console.warn('[FeatureFlags] Failed to set runtime flag:', e);
  }
}

/**
 * Get runtime feature flag
 * 
 * @param flagName - Feature flag name
 * @returns Feature flag value or undefined if not set
 */
export function getRuntimeFeatureFlag(flagName: string): boolean | string | undefined {
  const flags = getRuntimeFlags();
  return flags[flagName];
}

/**
 * Clear runtime feature flag
 * 
 * @param flagName - Feature flag name
 */
export function clearRuntimeFeatureFlag(flagName: string): void {
  try {
    const flags = getRuntimeFlags();
    delete flags[flagName];
    sessionStorage.setItem(RUNTIME_FLAGS_KEY, JSON.stringify(flags));
  } catch (e) {
    console.warn('[FeatureFlags] Failed to clear runtime flag:', e);
  }
}

/**
 * Get feature flag value with precedence: runtime > build-time > default
 * 
 * MUST FIX #4: Runtime flag must override build-time flag
 * 
 * @param flagName - Feature flag name (e.g., 'MSAL_USE_REDIRECT')
 * @param buildTimeEnvVar - Build-time environment variable name (e.g., 'VITE_MSAL_USE_REDIRECT')
 * @param defaultValue - Default value if neither runtime nor build-time flag is set
 * @returns Feature flag value
 */
export function getFeatureFlag(
  flagName: string,
  buildTimeEnvVar: string,
  defaultValue: boolean = false
): boolean {
  // Priority 1: Check runtime flag first (MUST FIX #4)
  const runtimeValue = getRuntimeFeatureFlag(flagName);
  if (runtimeValue !== undefined) {
    return typeof runtimeValue === 'boolean' ? runtimeValue : runtimeValue === 'true';
  }

  // Priority 2: Check build-time environment variable
  const buildTimeValue = import.meta.env[buildTimeEnvVar];
  if (buildTimeValue !== undefined) {
    return buildTimeValue !== 'false' && buildTimeValue !== '0';
  }

  // Priority 3: Use default value
  return defaultValue;
}

/**
 * Check if MSAL redirect flow should be used
 * 
 * Runtime flag: 'MSAL_USE_REDIRECT'
 * Build-time env: VITE_MSAL_USE_REDIRECT
 * Default: true (for new implementation)
 */
export function shouldUseMsalRedirect(): boolean {
  return getFeatureFlag('MSAL_USE_REDIRECT', 'VITE_MSAL_USE_REDIRECT', true);
}

/**
 * Check if MSAL popup fallback should be enabled
 * 
 * Runtime flag: 'MSAL_POPUP_FALLBACK'
 * Build-time env: VITE_ENABLE_MSAL_POPUP_FALLBACK
 * Default: false (popup is deprecated)
 */
export function shouldEnableMsalPopupFallback(): boolean {
  return getFeatureFlag('MSAL_POPUP_FALLBACK', 'VITE_ENABLE_MSAL_POPUP_FALLBACK', false);
}
